import { Injectable } from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, merge } from 'rxjs';
import { map, mapTo, startWith } from 'rxjs/operators';
import { rotateCursor } from '../../cross-team-shared/cursors/custom-cursors';
import { KeyboardShortcutService } from '../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { Axis } from '../renderers/side-view/side-view.component';
import { PointCloudContentStore } from '../state/point-cloud-content-store';

export const viewTitles = {
  default: {
    2: 'top',
    1: 'back',
    0: 'side',
  },
  rotating: {
    2: 'yaw',
    1: 'roll',
    0: 'pitch',
  },
  eventTracking: {
    2: 'top',
    1: 'middle',
    0: 'bottom',
  },
} as const;

@Injectable()
export class SideViewRotationService extends InjectableSuperclass {
  private dragInProgress$ = new BehaviorSubject(false);

  constructor(
    private keyboardShortcutService: KeyboardShortcutService,
    private store: PointCloudContentStore,
  ) {
    super();
  }

  getIsInRotationMode$() {
    return combineLatest([this.buildShiftDown$(), this.dragInProgress$]).pipe(
      map(([shiftDown, dragInProgress]) => {
        if (this.store.state().readOnly) {
          return false;
        } else {
          return shiftDown || dragInProgress;
        }
      }),
    );
  }

  setDragInProgress(isDragInProgress: boolean) {
    this.dragInProgress$.next(isDragInProgress);
  }

  getRotationCursor$() {
    return this.getIsInRotationMode$().pipe(
      map((isRotating) => (isRotating ? rotateCursor : null)),
    );
  }

  getViewTitle$(axis: Axis) {
    return this.getIsInRotationMode$().pipe(
      map(
        (isRotating) => viewTitles[isRotating ? 'rotating' : 'default'][axis],
      ),
    );
  }

  private buildShiftDown$() {
    return merge(
      this.keyboardShortcutService
        .getMatchingEvent$('shiftDown')
        .pipe(mapTo(true)),
      this.keyboardShortcutService
        .getMatchingEvent$('shiftUp')
        .pipe(mapTo(false)),
    ).pipe(startWith(false));
  }
}
